import courseCompletionMitraAPI from '@/api/course-completion-mitra/course-completion-mitra.js';
import { normalizeErrorMessage } from '@/utils/error-message';
import { normalizeBatchList } from '@/utils/batchIdentity';

const state = () => ({
  courses: [],
  courseDetail: null,
  summary: {
    this_year: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    },
    history: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    }
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 12,
    totalPage: 0
  },
  loading: false,
  summaryLoading: false,
  error: null
});

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_SUMMARY(state, summary) {
    state.summary = summary;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total || 0,
      page: pagination.page || 1,
      limit: pagination.limit || 12,
      totalPage: pagination.totalPage || 0
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  UPDATE_COURSE_STATUS(state, { courseId, status, certificate_path }) {
    const course = state.courses.find(c => c.id === courseId);
    if (course) {
      course.status = status;
      if (certificate_path) {
        course.certificate_path = certificate_path;
      }
    }
  }
};

const getters = {
  courses: (state) => state.courses,
  courseDetail: (state) => state.courseDetail,
  summary: state => state.summary || {
    this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
    history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
  },
  pagination: (state) => state.pagination,
  loading: (state) => state.loading,
  summaryLoading: (state) => state.summaryLoading,
  error: (state) => state.error
};

const actions = {
  async getCourses({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const response = await courseCompletionMitraAPI.getCourses(params);
      // API response structure: { httpCode, httpMessage, message, data: { data: [], total, page, limit, totalPage }, error }
      const data = response.data.data;
      
      commit('SET_COURSES', data.data || []);
      commit('SET_PAGINATION', {
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 12,
        totalPage: data.totalPage || 0
      });
      
      return data;
    } catch (error) {
      console.error('Error fetching courses:', error);
      commit('SET_ERROR', normalizeErrorMessage(error.message || 'Failed to fetch courses'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit, state }, courseId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      // Call both endpoints in parallel
      const [batchesResponse, catalogResponse] = await Promise.all([
        courseCompletionMitraAPI.getCourseDetail(courseId),
        courseCompletionMitraAPI.getCourseDetailFromCatalog(courseId)
      ]);
      
      // Extract batches data
      const batches = normalizeBatchList(batchesResponse.data.data);
      
      // Extract catalog detail data
      const catalogDetail = catalogResponse.data.data || {};
      
      // Find the course from the courses list to get enrollment info
      const course = state.courses.find(c => c.id === courseId);
      
      // Merge all data: course enrollment info + catalog detail + batches
      const courseDetailWithBatches = {
        ...(course || {}),
        ...catalogDetail,
        batches: batches
      };
      
      commit('SET_COURSE_DETAIL', courseDetailWithBatches);
      return courseDetailWithBatches;
    } catch (error) {
      console.error('Error fetching course detail:', error);
      commit('SET_ERROR', normalizeErrorMessage(error.message || 'Failed to fetch course detail'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getSummary({ commit }, params = {}) {
    commit('SET_SUMMARY_LOADING', true);
    
    try {
      const response = await courseCompletionMitraAPI.getSummary(params);
      // API might return response.data.data or response.data directly
      const summaryData = response.data.data || response.data;
      commit('SET_SUMMARY', summaryData);
      return summaryData;
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  },

  async uploadCertificate({ commit }, { courseId, formData }) {
    commit('SET_LOADING', true);
    
    try {
      const response = await courseCompletionMitraAPI.uploadCertificate(courseId, formData);
      
      // Update course status locally
      commit('UPDATE_COURSE_STATUS', {
        courseId,
        status: 'in_review'
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading certificate:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async submitCompletion({ commit }, { enrollmentId, formData }) {
    commit('SET_LOADING', true);
    
    try {
      const response = await courseCompletionMitraAPI.submitCompletion(enrollmentId, formData);
      return response.data;
    } catch (error) {
      console.error('Error submitting completion:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async downloadCertificate({ commit }, courseId) {
    try {
      const response = await courseCompletionMitraAPI.downloadCertificate(courseId);
      return response.data;
    } catch (error) {
      console.error('Error downloading certificate:', error);
      throw error;
    }
  },

  clearCourseDetail({ commit }) {
    commit('SET_COURSE_DETAIL', null);
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
